export type NativeDisplayDensity = 'more-compact' | 'compact' | 'comfortable' | 'large';

const STORAGE_KEY = 'qixit.native.display.v1';
const BASE_HTML_FONT_PX = 16;

export const NATIVE_DISPLAY_DENSITY_OPTIONS: {
  value: NativeDisplayDensity;
  label: string;
  textScale: number;
}[] = [
  { value: 'more-compact', label: 'More compact', textScale: 0.6875 },
  { value: 'compact', label: 'Compact', textScale: 0.8125 },
  { value: 'comfortable', label: 'Comfortable', textScale: 1 },
  { value: 'large', label: 'Large', textScale: 1.25 },
];

export const DEFAULT_NATIVE_DISPLAY_DENSITY: NativeDisplayDensity = 'compact';

export function isNativeDisplayDensity(value: string | null | undefined): value is NativeDisplayDensity {
  return (
    value === 'more-compact' ||
    value === 'compact' ||
    value === 'comfortable' ||
    value === 'large'
  );
}

export function getNativeDisplayDensityConfig(density: NativeDisplayDensity) {
  return (
    NATIVE_DISPLAY_DENSITY_OPTIONS.find((option) => option.value === density) ??
    NATIVE_DISPLAY_DENSITY_OPTIONS.find((option) => option.value === DEFAULT_NATIVE_DISPLAY_DENSITY)!
  );
}

export function getNativeHtmlFontSizePx(density: NativeDisplayDensity): number {
  const config = getNativeDisplayDensityConfig(density);
  return Math.round(BASE_HTML_FONT_PX * config.textScale * 100) / 100;
}

export function loadNativeDisplayDensity(): NativeDisplayDensity {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (isNativeDisplayDensity(raw)) {
      return raw;
    }
  } catch {
    // Ignore private-mode / quota errors.
  }
  return DEFAULT_NATIVE_DISPLAY_DENSITY;
}

export function saveNativeDisplayDensity(density: NativeDisplayDensity): void {
  try {
    localStorage.setItem(STORAGE_KEY, density);
  } catch {
    // Ignore private-mode / quota errors.
  }
}

export function applyNativeDisplayDensityToDocument(density: NativeDisplayDensity): void {
  if (typeof document === 'undefined') return;
  const config = getNativeDisplayDensityConfig(density);
  const htmlFontPx = getNativeHtmlFontSizePx(density);
  document.documentElement.style.setProperty('--qixit-text-scale', String(config.textScale));
  document.documentElement.style.fontSize = `${htmlFontPx}px`;
}
